

/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { Download, Filter, BookmarkCheck, Plus, Trash2, Save, X, Columns3 } from 'lucide-react';

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import MDButton from "components/MDButton";
import PearlLogoLoader from "components/PearlLogoLoader";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import { 
  Button, 
  Tooltip, 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemText, 
  CircularProgress,
  Drawer,
  Divider,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ListItemIcon,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import useColumnResize from "examples/Tables/DataTable/useColumnResize";

// Auth context for API calls
import { useAuth } from "auth/Auth";
import { applyConditions, normalizeCondition, normalizeObjectType, normalizeFieldType } from "utils/filterMatch";
import { formatDate } from "utils/DateTimeConversion";
import tokens from "styles/tokens/colors";
import { useTheme } from "@mui/material/styles";
import { v2 as v2t, v2Density, v2Tones } from "styles/tokens/v2";
import { FilterSearch, FilterSelect } from "components/v2/filters";

// Columns the chooser never offers (row actions are not data)
const isChoosableColumn = (column) =>
  Boolean(column.id) && column.id !== "actions" && !column.disableColumnChooser;

const readJSON = (key) => {
  if (!key) return null;
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
};

// Cell for a tenant custom field (cf1..cfN) shown through the column chooser
const renderCustomFieldValue = (value, type) => {
  if (value === null || value === undefined || value === "") return "-";
  if (type === "checkbox") {
    const truthy = value === true || String(value).toLowerCase() === "true";
    return truthy ? "Yes" : "No";
  }
  if (type === "date" || type === "datetime") return formatDate(value, type);
  return String(value);
};

// UI v2: the toolbar's Filter and Saved filters icon buttons as 36px v2 controls.
const v2IconButtonSx = (active) => ({
  width: v2Density.controlHeightPx,
  height: v2Density.controlHeightPx,
  border: `1px solid ${active ? v2t.primary : v2t.border2}`,
  borderRadius: `${v2Density.radiusPx}px`,
  color: active ? v2Tones.info.fg : v2t.text2,
  backgroundColor: active ? v2Tones.info.bg : v2t.surface,
  "&:hover": {
    backgroundColor: active ? v2Tones.info.bg : v2t.hover,
    borderColor: active ? v2t.primary : v2t.text3,
  },
  "&.Mui-focusVisible": { outline: `2px solid ${v2t.link}`, outlineOffset: 1 },
});

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  exportCSV,
  csvFields,
  objectType: objectTypeProp,
  onFilterApply,
  onFilterClear,
  showFilters,
  onFilterIconClick,
  columnChooser,
}) {

  // Get axios instance from auth context
  const { axiosInstance, user } = useAuth();
  // Filters, metadata and preferences are keyed by the singular object ('deals' -> 'deal')
  const objectType = normalizeObjectType(objectTypeProp);

  // State for saved filters dropdown
  const [savedFilterAnchorEl, setSavedFilterAnchorEl] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const openSavedFilterMenu = Boolean(savedFilterAnchorEl);

  // State for filter drawer
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterConditions, setFilterConditions] = useState([
    { field: '', operator: '', value: '' }
  ]);
  const [logicOperator, setLogicOperator] = useState('AND');
  const [availableFields, setAvailableFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  // True once the field metadata request has finished (success or failure).
  // A restored or saved filter is only evaluated after this, so field types are known.
  const [fieldsSettled, setFieldsSettled] = useState(!objectType);
  const fieldsRequestedRef = useRef(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  
  // State for users (for autocomplete in reference fields)
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // State for reference data (accounts, contacts, orders, etc.)
  const [referenceData, setReferenceData] = useState({}); // { 'accounts': [...], 'contacts': [...] }
  const [loadingReferenceData, setLoadingReferenceData] = useState({}); // { 'accounts': false, 'contacts': false }

  // State for save filter dialog
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);

  // Generate storage key for this table's filters
  const filterStorageKey = objectType ? `dataTable_filter_${objectType}` : null;

  // State for filtered data - initialize from localStorage if available
  const [appliedFilters, setAppliedFilters] = useState(() => {
    if (filterStorageKey) {
      try {
        const stored = localStorage.getItem(filterStorageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed.appliedFilters || null;
        }
      } catch (e) {
        console.error('Error loading filters from localStorage:', e);
      }
    }
    return null;
  });

  // Keep the filter icon highlighted for a filter restored from localStorage
  useEffect(() => {
    if (appliedFilters?.conditions?.length && activeFiltersCount === 0) {
      setActiveFiltersCount(appliedFilters.conditions.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for selected filter - initialize from localStorage
  const [storedSelectedFilterId, setStoredSelectedFilterId] = useState(() => {
    if (filterStorageKey) {
      try {
        const stored = localStorage.getItem(filterStorageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed.selectedFilterId || null;
        }
      } catch (e) {
        console.error('Error loading selected filter from localStorage:', e);
      }
    }
    return null;
  });
  
  // State for editing filters
  const [editingFilter, setEditingFilter] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState(null);
  const [deletingFilter, setDeletingFilter] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'success',
  });

  const showFilterFeedback = (message, title = 'Filter', severity = 'success') => {
    setFeedbackDialog({ open: true, title, message, severity });
  };

  const closeFilterFeedback = () => {
    setFeedbackDialog((prev) => ({ ...prev, open: false }));
  };

  // Save filters to localStorage when they change
  useEffect(() => {
    if (filterStorageKey) {
      try {
        const dataToStore = {
          appliedFilters,
          selectedFilterId: selectedFilter?.id || null,
          filterConditions,
          logicOperator,
          activeFiltersCount
        };
        localStorage.setItem(filterStorageKey, JSON.stringify(dataToStore));
      } catch (e) {
        console.error('Error saving filters to localStorage:', e);
      }
    }
  }, [appliedFilters, selectedFilter, filterConditions, logicOperator, activeFiltersCount, filterStorageKey]);

  // Restore selected filter from saved filters when they load
  useEffect(() => {
    if (storedSelectedFilterId && savedFilters.length > 0) {
      const filter = savedFilters.find(f => f.id === storedSelectedFilterId);
      if (filter && !selectedFilter) {
        setSelectedFilter(filter);
        if (filter.conditions) {
          setFilterConditions(filter.conditions.map(raw => {
            const c = normalizeCondition(raw, objectType);
            return { field: c.field, operator: c.operator, value: c.value || '' };
          }));
          setLogicOperator(filter.logic_operator || 'AND');
          setActiveFiltersCount(filter.conditions.length);
        }
      }
    }
  }, [savedFilters, storedSelectedFilterId]);

  // Cache for operators per field type (loaded from backend /metadata/operators/{type})
  const [operatorsCache, setOperatorsCache] = useState({});

  // Fetch saved filters from backend when objectType is provided
  const fetchSavedFilters = useCallback(async () => {
    if (!objectType || !axiosInstance) return;
    
    setLoadingFilters(true);
    try {
      const response = await axiosInstance.get(`/filters/by-object/${objectType}`);
      setSavedFilters(response.data || []);
    } catch (error) {
      console.error("Error fetching saved filters:", error);
      setSavedFilters([]);
    } finally {
      setLoadingFilters(false);
    }
  }, [objectType, axiosInstance]);

  // Fetch available fields for filtering from metadata endpoint
  const fetchAvailableFields = useCallback(async () => {
    if (!objectType || !axiosInstance || fieldsRequestedRef.current) return;
    fieldsRequestedRef.current = true;

    setLoadingFields(true);
    try {
      // Filters metadata (standard + custom fields) first; the metadata router
      // has no prefix, so its fallback lives at /fields/{object}
      let response;
      try {
        response = await axiosInstance.get(`/filters/metadata/fields/${objectType}`);
      } catch (err) {
        response = await axiosInstance.get(`/fields/${objectType}`);
      }
      
      const fieldsData = response.data;
      
      // Handle both array and object responses
      if (Array.isArray(fieldsData)) {
        setAvailableFields(fieldsData);
      } else if (typeof fieldsData === 'object') {
        // Convert object format to array format
        const fieldsArray = Object.entries(fieldsData)
          .filter(([, fieldDef]) => fieldDef.filterable !== false)
          .map(([name, fieldDef]) => ({
            name,
            label: fieldDef.label || name,
            type: normalizeFieldType(fieldDef.type),
            options: fieldDef.options || [],
            reference_to: fieldDef.reference_to,
            required: fieldDef.required || false,
            is_custom: fieldDef.is_custom || false,
          }));
        setAvailableFields(fieldsArray);
      } else {
        setAvailableFields([]);
      }
    } catch (error) {
      // Not every objectType has metadata (some travel lists); filters then run as text
      console.warn("Could not load filter fields:", error?.message);
      fieldsRequestedRef.current = false;
      setAvailableFields([]);
    } finally {
      setLoadingFields(false);
      setFieldsSettled(true);
    }
  }, [objectType, axiosInstance]);

  // Fetch filters when component mounts or objectType changes
  useEffect(() => {
    if (objectType && showFilters !== false) {
      fetchSavedFilters();
    }
  }, [objectType, fetchSavedFilters, showFilters]);

  // Fetch fields on mount: a filter restored from localStorage, a saved filter
  // and the column chooser all need the field types, not just the drawer.
  // Opening the drawer retries after a failed load.
  useEffect(() => {
    if (objectType) {
      fetchAvailableFields();
    }
  }, [objectType, fetchAvailableFields, filterDrawerOpen]);

  const fieldMap = useMemo(
    () => Object.fromEntries(availableFields.map((f) => [f.name, f])),
    [availableFields]
  );

  // Fetch users for autocomplete (when filter drawer opens)
  const fetchUsers = useCallback(async () => {
    if (!axiosInstance || users.length > 0) return; // Only fetch once
    
    setLoadingUsers(true);
    try {
      const response = await axiosInstance.get('/users/');
      const usersList = (response.data || []).map(user => ({
        id: user.id,
        label: user.fullname || user.username || user.name || 'Unknown User',
        username: user.username,
        email: user.email
      }));
      setUsers(usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [axiosInstance, users.length]);

  // Fetch users when filter drawer opens
  useEffect(() => {
    if (filterDrawerOpen) {
      fetchUsers();
    }
  }, [filterDrawerOpen, fetchUsers]);

  // Fetch reference data for a specific reference type
  const fetchReferenceData = useCallback(async (referenceType, searchQuery = '') => {
    if (!referenceType || !axiosInstance) return;
    
    // Always fetch if there's a search query, or if we don't have data yet
    const hasData = referenceData[referenceType] && referenceData[referenceType].length > 0;
    if (hasData && !searchQuery) {
      return; // Skip if we already have data and no search query
    }
    
    setLoadingReferenceData(prev => ({ ...prev, [referenceType]: true }));
    try {
      const params = searchQuery ? { search: searchQuery, limit: 50 } : { limit: 100 };
      const response = await axiosInstance.get(`/reference-data/${referenceType}`, { params });
      
      // Transform the response to match our format { id, label }
      const transformedData = (response.data || []).map(item => {
        // Handle both {value, label} and {id, name} formats
        const id = item.value !== undefined ? item.value : (item.id !== undefined ? item.id : null);
        const label = item.label !== undefined ? item.label : (item.name !== undefined ? item.name : 'Unknown');
        return {
          id: id,
          label: label
        };
      }).filter(item => item.id !== null); // Filter out invalid items
      
      setReferenceData(prev => ({
        ...prev,
        [referenceType]: searchQuery ? transformedData : transformedData // For search, replace; otherwise merge or replace
      }));
    } catch (error) {
      console.error(`Error fetching reference data for ${referenceType}:`, error);
      setReferenceData(prev => ({
        ...prev,
        [referenceType]: []
      }));
    } finally {
      setLoadingReferenceData(prev => ({ ...prev, [referenceType]: false }));
    }
  }, [axiosInstance, referenceData]);

  // Fetch reference data when a reference field is selected
  useEffect(() => {
    if (!filterDrawerOpen || availableFields.length === 0) return;
    
    // Find all unique reference types in current filter conditions
    const referenceTypes = new Set();
    filterConditions.forEach(condition => {
      if (condition.field) {
        const field = availableFields.find(f => f.name === condition.field);
        if (field?.type === 'reference' && field?.reference_to) {
          referenceTypes.add(field.reference_to);
        }
      }
    });
    
    // Fetch data for each reference type
    referenceTypes.forEach(refType => {
      if (refType === 'users') {
        // Users are already fetched separately
        if (users.length === 0) {
          fetchUsers();
        }
      } else {
        // Fetch other reference types if not already loaded
        if (!referenceData[refType] || referenceData[refType].length === 0) {
          fetchReferenceData(refType);
        }
      }
    });
  }, [filterDrawerOpen, filterConditions, availableFields, fetchReferenceData, users.length, referenceData]);

  const handleFilterIconClick = () => {
    // When user clicks filter icon, always open a fresh panel to create NEW filter
    resetFilterPanel();
    if (onFilterIconClick) {
      onFilterIconClick();
    } else {
      setFilterDrawerOpen(true);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerOpen(false);
    // Reset editing state when closing drawer
    if (editingFilter) {
      setEditingFilter(null);
      setFilterName('');
      setFilterDescription('');
    }
  };

  const handleSavedFilterClick = (event) => {
    setSavedFilterAnchorEl(event.currentTarget);
  };

  const handleSavedFilterClose = () => {
    setSavedFilterAnchorEl(null);
  };

  const handleFilterSelect = async (filter) => {
    setSelectedFilter(filter);
    handleSavedFilterClose();
    
    // Apply the saved filter to table data but DO NOT pre-fill panel
    if (filter.conditions && filter.conditions.length > 0) {
      const filterDataObj = {
        conditions: filter.conditions,
        logic_operator: filter.logic_operator || 'AND'
      };
      setAppliedFilters(filterDataObj);
      setActiveFiltersCount(filter.conditions.length);
    }
    
    // Call the parent's filter apply handler if provided
    if (onFilterApply) {
      onFilterApply(filter);
    }
  };

  // Reset only the panel (for creating a new filter), do NOT touch applied filter
  const resetFilterPanel = () => {
    setFilterConditions([{ field: '', operator: '', value: '' }]);
    setLogicOperator('AND');
    setFilterName('');
    setFilterDescription('');
    setEditingFilter(null);
  };

  // Clear the applied filter (used from Saved Filters menu)
  const handleClearFilter = () => {
    setSelectedFilter(null);
    setFilterConditions([{ field: '', operator: '', value: '' }]);
    setLogicOperator('AND');
    setActiveFiltersCount(0);
    setAppliedFilters(null); // Clear applied filters
    setStoredSelectedFilterId(null);
    handleSavedFilterClose();
    
    // Clear from localStorage
    if (filterStorageKey) {
      localStorage.removeItem(filterStorageKey);
    }
    
    // Call the parent's filter clear handler if provided
    if (onFilterClear) {
      onFilterClear();
    }
  };

  // Add a new condition
  const addCondition = () => {
    setFilterConditions([...filterConditions, { field: '', operator: '', value: '' }]);
  };

  // Remove a condition
  const removeCondition = (index) => {
    if (filterConditions.length > 1) {
      const newConditions = filterConditions.filter((_, i) => i !== index);
      setFilterConditions(newConditions);
    }
  };

  // Update a condition
  const updateCondition = (index, key, value) => {
    const newConditions = [...filterConditions];
    newConditions[index] = { ...newConditions[index], [key]: value };
    
    // Reset operator and value when field changes
    if (key === 'field') {
      newConditions[index].operator = '';
      newConditions[index].value = '';
      
      // If the new field is a reference field, fetch its data
      const field = availableFields.find(f => f.name === value);
      if (field?.type === 'reference' && field?.reference_to) {
        const referenceType = field.reference_to;
        if (referenceType === 'users') {
          fetchUsers();
        } else {
          fetchReferenceData(referenceType);
        }
      }
    }
    
    setFilterConditions(newConditions);
  };

  // Get operators for a field (from backend cache)
  const getOperatorsForField = (fieldName) => {
    const field = availableFields.find(f => f.name === fieldName);
    const fieldType = field?.type || 'text';
    const operators = operatorsCache[fieldType];
    // If operators not yet loaded, return empty list to avoid hardcoding
    return operators || [];
  };

  // Get field options (for picklist fields)
  const getFieldOptions = (fieldName) => {
    const field = availableFields.find(f => f.name === fieldName);
    return field?.options || [];
  };

  // Get field type
  const getFieldType = (fieldName) => {
    const field = availableFields.find(f => f.name === fieldName);
    return field?.type || 'text';
  };

  // Check if field is any reference field
  const isReferenceField = (fieldName) => {
    const field = availableFields.find(f => f.name === fieldName);
    return field?.type === 'reference' && field?.reference_to;
  };

  // Check if operator needs a value
  const operatorNeedsValue = (operator) => {
    return !['is_empty', 'is_not_empty'].includes(operator);
  };

  // Fetch operators for a given field type from backend metadata
  const fetchOperatorsForType = useCallback(
    async (fieldType) => {
      if (!fieldType || operatorsCache[fieldType]) return;
      if (!axiosInstance) return;
      try {
        // Primary: use Filters module metadata endpoint
        let response = await axiosInstance.get(`/filters/metadata/operators/${fieldType}`);
        let ops = response.data;

        // Fallbacks if response is not an array
        if (!Array.isArray(ops)) {
          try {
            response = await axiosInstance.get(`/operators/${fieldType}`);
            ops = response.data;
          } catch {
            ops = null;
          }
        }

        if (Array.isArray(ops)) {
          setOperatorsCache((prev) => ({
            ...prev,
            [fieldType]: ops,
          }));
        }
      } catch (error) {
        console.error(`Error fetching operators for type ${fieldType}:`, error);
      }
    },
    [axiosInstance, operatorsCache]
  );

  // When fields are available and drawer opens, prefetch operators for all used types
  useEffect(() => {
    if (!filterDrawerOpen || availableFields.length === 0) return;
    const uniqueTypes = Array.from(new Set(availableFields.map((f) => f.type || 'text')));
    uniqueTypes.forEach((t) => {
      fetchOperatorsForType(t);
    });
  }, [filterDrawerOpen, availableFields, fetchOperatorsForType]);

  // Apply filters
  const applyFilters = () => {
    // Same rule as the Kanban drawer: a value is required unless the operator takes none
    const validConditions = filterConditions.filter(
      c => c.field && c.operator && (!operatorNeedsValue(c.operator) || (c.value !== '' && c.value != null))
    );
    
    if (validConditions.length === 0) {
      // No conditions in panel; just close drawer, keep any existing applied filter
      handleFilterDrawerClose();
      return;
    }

    setActiveFiltersCount(validConditions.length);
    setSelectedFilter(null); // Clear saved filter selection since we're using custom conditions

    const filterDataObj = {
      conditions: validConditions,
      logic_operator: logicOperator
    };

    // Apply filters to the table data
    setAppliedFilters(filterDataObj);

    if (onFilterApply) {
      onFilterApply(filterDataObj);
    }

    handleFilterDrawerClose();
  };

  // Open save dialog
  const openSaveDialog = () => {
    const validConditions = filterConditions.filter(c => c.field && c.operator);
    if (validConditions.length === 0) {
      showFilterFeedback(
        'Please add at least one filter condition before saving.',
        'Filter Required',
        'info'
      );
      return;
    }
    setSaveDialogOpen(true);
  };

  // Save filter to backend
  const saveFilter = async () => {
    if (!filterName.trim()) {
      showFilterFeedback('Please enter a filter name.', 'Filter Name Required', 'info');
      return;
    }

    const validConditions = filterConditions.filter(c => c.field && c.operator);
    if (validConditions.length === 0) {
      showFilterFeedback('Please add at least one filter condition.', 'Filter Required', 'info');
      return;
    }

    setSavingFilter(true);
    try {
      const response = await axiosInstance.post('/filters/', {
        name: filterName.trim(),
        description: filterDescription.trim() || null,
        object_type: objectType,
        conditions: validConditions,
        logic_operator: logicOperator,
        is_default: false
      });

      const savedFilter = response.data;
      const filterDataObj = {
        conditions: savedFilter?.conditions || validConditions,
        logic_operator: savedFilter?.logic_operator || logicOperator,
      };

      // Refresh saved filters list
      fetchSavedFilters();

      // Apply the newly created filter immediately
      setAppliedFilters(filterDataObj);
      setActiveFiltersCount(filterDataObj.conditions.length);
      setSelectedFilter(savedFilter);

      // Close dialogs and side panel
      setSaveDialogOpen(false);
      setFilterName('');
      setFilterDescription('');
      handleFilterDrawerClose();

      showFilterFeedback('Filter saved and applied successfully.', 'Filter Saved', 'success');
    } catch (error) {
      console.error("Error saving filter:", error);
      showFilterFeedback('Failed to save filter. Please try again.', 'Save Failed', 'error');
    } finally {
      setSavingFilter(false);
    }
  };

  // Refresh filters (can be called from parent)
  const refreshFilters = () => {
    fetchSavedFilters();
  };

  // Delete a saved filter
  const deleteFilter = async (filterId) => {
    setDeletingFilter(true);
    try {
      await axiosInstance.delete(`/filters/${filterId}`);
      
      // If the deleted filter was selected, clear it
      if (selectedFilter?.id === filterId) {
        handleClearFilter();
      }
      
      // Refresh the filters list
      fetchSavedFilters();
      setDeleteConfirmOpen(false);
      setFilterToDelete(null);
    } catch (error) {
      console.error("Error deleting filter:", error);
      showFilterFeedback('Failed to delete filter. Please try again.', 'Delete Failed', 'error');
    } finally {
      setDeletingFilter(false);
    }
  };

  // Open edit mode for a filter
  const handleEditFilter = (filter, event) => {
    event.stopPropagation();
    setEditingFilter(filter);
    
    // Load filter conditions into the drawer for editing (old field names mapped to current ones)
    if (filter.conditions) {
      setFilterConditions(filter.conditions.map(raw => {
        const c = normalizeCondition(raw, objectType);
        return { field: c.field, operator: c.operator, value: c.value || '' };
      }));
      setLogicOperator(filter.logic_operator || 'AND');
      setFilterName(filter.name);
      setFilterDescription(filter.description || '');
    }
    
    handleSavedFilterClose();
    setFilterDrawerOpen(true);
  };

  // Confirm delete filter
  const handleDeleteFilterClick = (filter, event) => {
    event.stopPropagation();
    setFilterToDelete(filter);
    setDeleteConfirmOpen(true);
    handleSavedFilterClose();
  };

  // Update existing filter
  const updateFilter = async () => {
    if (!filterName.trim()) {
      showFilterFeedback('Please enter a filter name.', 'Filter Name Required', 'info');
      return;
    }

    const validConditions = filterConditions.filter(c => c.field && c.operator);
    if (validConditions.length === 0) {
      showFilterFeedback('Please add at least one filter condition.', 'Filter Required', 'info');
      return;
    }

    setSavingFilter(true);
    try {
      const response = await axiosInstance.put(`/filters/${editingFilter.id}`, {
        name: filterName.trim(),
        description: filterDescription.trim() || null,
        object_type: objectType,
        conditions: validConditions,
        logic_operator: logicOperator,
        is_default: editingFilter.is_default || false
      });

      // Refresh saved filters list
      fetchSavedFilters();
      
      // Close dialogs and reset
      setSaveDialogOpen(false);
      setFilterName('');
      setFilterDescription('');
      setEditingFilter(null);
      
      // Apply the updated filter
      const filterDataObj = {
        conditions: validConditions,
        logic_operator: logicOperator
      };
      setAppliedFilters(filterDataObj);
      setSelectedFilter(response.data);
      setActiveFiltersCount(validConditions.length);
      
      handleFilterDrawerClose();
      showFilterFeedback('Filter updated and applied successfully.', 'Filter Updated', 'success');
    } catch (error) {
      console.error("Error updating filter:", error);
      showFilterFeedback('Failed to update filter. Please try again.', 'Update Failed', 'error');
    } finally {
      setSavingFilter(false);
    }
  };

  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const chooserEnabled = Boolean(objectType) && columnChooser !== false;

  // Ids of the columns the page defines (string accessors double as ids in react-table)
  const pageColumnIds = useMemo(() => {
    const ids = [];
    const walk = (cols) =>
      (cols || []).forEach((c) => {
        if (c.columns) walk(c.columns);
        const id = c.id || (typeof c.accessor === "string" ? c.accessor : null);
        if (id) ids.push(id);
      });
    walk(table.columns);
    return ids;
  }, [table.columns]);

  // Every metadata field the page does not already show (custom fields included)
  // becomes an optional column, hidden until the user turns it on.
  const extraFieldDefs = useMemo(() => {
    if (!chooserEnabled) return [];
    const taken = new Set(pageColumnIds);
    // A page column that renders a lookup as '<field>_name' (assigned_to_name)
    // already shows that field
    pageColumnIds.forEach((id) => {
      if (id.endsWith("_name")) taken.add(id.slice(0, -"_name".length));
    });
    // and one with the same label would read as a duplicate in the chooser
    const takenLabels = new Set();
    const walk = (cols) =>
      (cols || []).forEach((c) => {
        if (c.columns) walk(c.columns);
        if (typeof c.Header === "string") takenLabels.add(c.Header.trim().toLowerCase());
      });
    walk(table.columns);
    return availableFields.filter(
      (f) =>
        f.name &&
        !taken.has(f.name) &&
        !takenLabels.has(String(f.label || f.name).trim().toLowerCase())
    );
  }, [chooserEnabled, availableFields, pageColumnIds, table.columns]);

  // Column chooser choices (see the column chooser section below). Declared
  // here because whether an extra column is searchable depends on them.
  const columnPrefsKey = chooserEnabled && user?.id ? `dataTable_columns_${user.id}_${objectType}` : null;
  const [columnPrefs, setColumnPrefs] = useState({ hidden: [], shown: [] });
  const shownExtrasKey = columnPrefs.shown.join("|");

  const columns = useMemo(() => {
    if (extraFieldDefs.length === 0) return table.columns;
    const shownExtras = new Set(shownExtrasKey ? shownExtrasKey.split("|") : []);
    const extras = extraFieldDefs.map((field) => ({
      Header: field.label || field.name,
      id: field.name,
      accessor: (row) => row[field.name],
      isExtraField: true,
      // react-table's global filter searches every column, hidden or not. A
      // field the user has not put on screen must not match a search, or rows
      // would match on values nobody can see (cf1, account_id, created_by...).
      disableGlobalFilter: !shownExtras.has(field.name),
      Cell: ({ row }) => {
        const original = row.original || {};
        let value = original[field.name];
        if (field.type === "reference") {
          // Prefer the denormalised name the list rows usually carry
          const base = field.name.replace(/_id$/, "");
          const named = original[`${field.name}_name`] ?? original[`${base}_name`];
          if (named) value = named;
          else if (field.reference_to === "users" && value !== null && value !== undefined && value !== "") {
            value = users.find((u) => String(u.id) === String(value))?.label || value;
          }
        }
        return (
          <MDTypography variant="caption" color="text" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {renderCustomFieldValue(value, field.type)}
          </MDTypography>
        );
      },
    }));
    // Keep a trailing actions column last
    const base = [...table.columns];
    const actionsIndex = base.findIndex((c) => c.accessor === "actions" || c.id === "actions");
    if (actionsIndex === base.length - 1) {
      return [...base.slice(0, -1), ...extras, base[base.length - 1]];
    }
    return [...base, ...extras];
  }, [table.columns, extraFieldDefs, users, shownExtrasKey]);

  const rawData = useMemo(() => table.rows, [table]);

  // A restored/saved filter waits for the field types; until then show a loader
  const filtersPending = Boolean(appliedFilters?.conditions?.length) && !fieldsSettled;

  // Apply filters to the data (same matcher as the Kanban views)
  const data = useMemo(() => {
    if (filtersPending) return rawData;
    return applyConditions(rawData, appliedFilters, fieldMap, objectType);
  }, [rawData, appliedFilters, fieldMap, objectType, filtersPending]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, hiddenColumns: extraFieldDefs.map((f) => f.name) },
      // The column chooser below owns hiddenColumns. Without this, react-table
      // resets them to initialState whenever the columns array changes identity
      // (a parent re-render, or the users lookup loading for an Assigned To
      // column), and the screen stops matching the saved choice.
      autoResetHiddenColumns: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    visibleColumns,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    allColumns,
    setHiddenColumns,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  /* ------------------------------ column chooser ------------------------------ */
  // Per user and object, per browser. Stored as overrides of the defaults
  // (page columns shown, extra fields hidden) so a field added later still
  // starts hidden instead of being frozen by an earlier write.
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  useEffect(() => {
    const stored = readJSON(columnPrefsKey);
    setColumnPrefs({
      hidden: Array.isArray(stored?.hidden) ? stored.hidden : [],
      shown: Array.isArray(stored?.shown) ? stored.shown : [],
    });
  }, [columnPrefsKey]);

  const extraIdsKey = extraFieldDefs.map((f) => f.name).join("|");
  const pageIdsKey = pageColumnIds.join("|");
  // Layout effect so newly loaded extra fields never paint as visible first
  useLayoutEffect(() => {
    const known = new Set([...pageColumnIds, ...extraFieldDefs.map((f) => f.name)]);
    const shown = new Set(columnPrefs.shown);
    const hidden = new Set(columnPrefs.hidden.filter((id) => known.has(id)));
    extraFieldDefs.forEach((f) => {
      if (!shown.has(f.name)) hidden.add(f.name);
    });
    hidden.delete("actions");
    setHiddenColumns(Array.from(hidden));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnPrefs, extraIdsKey, pageIdsKey, setHiddenColumns]);

  const choosableColumns = useMemo(
    () => (allColumns || []).filter(isChoosableColumn),
    [allColumns]
  );

  const toggleColumn = (column) => {
    const id = column.id;
    const makeVisible = !column.isVisible;
    const next = {
      hidden: columnPrefs.hidden.filter((h) => h !== id),
      shown: columnPrefs.shown.filter((h) => h !== id),
    };
    if (makeVisible && column.isExtraField) next.shown.push(id);
    if (!makeVisible && !column.isExtraField) next.hidden.push(id);
    // Never leave the table with no data column
    const visibleCount = choosableColumns.filter((c) => c.isVisible).length;
    if (!makeVisible && visibleCount <= 1) return;
    setColumnPrefs(next);
    if (columnPrefsKey) {
      try {
        localStorage.setItem(columnPrefsKey, JSON.stringify(next));
      } catch (e) {
        // Private mode / quota: the choice still applies for this session
      }
    }
  };

  const resetColumns = () => {
    const next = { hidden: [], shown: [] };
    setColumnPrefs(next);
    if (columnPrefsKey) {
      try {
        localStorage.removeItem(columnPrefsKey);
      } catch (e) {
        // ignore
      }
    }
  };

  // Users referenced by a visible extra column (e.g. Updated By) need their names
  const needsUserNames = choosableColumns.some(
    (c) => c.isExtraField && c.isVisible && fieldMap[c.id]?.reference_to === "users"
  );
  useEffect(() => {
    if (needsUserNames) fetchUsers();
  }, [needsUserNames, fetchUsers]);

  // Drag-to-resize column widths, persisted per object type
  const tableRef = useRef(null);
  const { columnWidths, startResize, resetColumn } = useColumnResize({
    storageKey: objectType ? `dataTable_colWidths_${objectType}` : null,
    columns: visibleColumns,
    tableRef,
  });
  const fixedLayout = Boolean(columnWidths);
  const fixedTableWidth = fixedLayout
    ? visibleColumns.reduce((total, column) => total + (columnWidths[column.id] || 150), 0)
    : undefined;

  const flattenExportValue = useCallback((node) => {
    if (node == null) return "";
    if (typeof node === "boolean") return node ? "Yes" : "No";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (node instanceof Date) return node.toISOString();
    if (Array.isArray(node)) {
      return node.map(flattenExportValue).filter(Boolean).join(" ");
    }
    if (typeof node === "object" && node.props) {
      const props = node.props;
      // Prefer explicit display props (StatusBadge label, PhoneCell value) before children.
      if (props.label != null && props.label !== "") return String(props.label);
      if (props.value != null && (typeof props.value === "string" || typeof props.value === "number") && String(props.value) !== "") {
        return String(props.value);
      }
      if (props.status != null && props.status !== "") return String(props.status);
      if (props.title != null && props.title !== "") return String(props.title);
      if (props.children != null) {
        const fromChildren = flattenExportValue(props.children);
        if (fromChildren) return fromChildren;
      }
      // Empty string value still counts (e.g. intentionally blank fields)
      if (props.value != null && (typeof props.value === "string" || typeof props.value === "number")) {
        return String(props.value);
      }
    }
    return "";
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!exportCSV) return;

    const visibility = new Map((allColumns || []).map((c) => [c.id, c.isVisible]));
    const exportColumns = (columns || []).filter((column) => {
      const id = column.id || (typeof column.accessor === "string" ? column.accessor : null);
      if (!id) return false;
      // Export what is on screen: columns hidden with the chooser stay out
      if (visibility.get(id) === false) return false;
      const header = String(column.Header || "").toLowerCase().trim();
      if (
        id === "actions" ||
        header === "actions" ||
        header === "action" ||
        column.disableExport
      ) {
        return false;
      }
      if (csvFields && csvFields.length > 0) {
        return csvFields.includes(id) || csvFields.includes(column.accessor);
      }
      return true;
    });

    if (!exportColumns.length) return;

    // Match on-screen list: advanced filters + search + current sort (all matching rows, not just the page)
    const rowsToExport = rows || [];

    const headers = exportColumns.map((column) => {
      if (typeof column.Header === "string" || typeof column.Header === "number") {
        return String(column.Header);
      }
      return column.id || (typeof column.accessor === "string" ? column.accessor : "column");
    });

    const csvRows = rowsToExport.map((row) => {
      prepareRow(row);
      return exportColumns.map((column) => {
        const id = column.id || (typeof column.accessor === "string" ? column.accessor : null);
        const cell = row.cells.find((c) => c.column.id === id);
        let value = "";
        let fromCell = false;

        if (cell) {
          try {
            value = flattenExportValue(cell.render("Cell"));
            fromCell = true;
          } catch (err) {
            value = "";
          }
        }

        // Only fall back when the Cell path produced nothing (or failed). Keep
        // intentional empty Cell output (e.g. "-" already stringified) as-is.
        if (!fromCell || value === "" || value == null) {
          if (id && row.values && Object.prototype.hasOwnProperty.call(row.values, id) && row.values[id] != null) {
            value = flattenExportValue(row.values[id]);
          } else if (typeof column.accessor === "function") {
            try {
              value = flattenExportValue(column.accessor(row.original, 0, { subRows: [], depth: 0, data: [] }));
            } catch (err) {
              value = "";
            }
          } else if (typeof column.accessor === "string") {
            const raw = row.original?.[column.accessor];
            if (raw != null) value = flattenExportValue(raw);
          }
        }

        return `"${String(value ?? "").replace(/"/g, '""')}"`;
      }).join(",");
    });

    const csvContent = [
      headers.map((header) => `"${String(header).replace(/"/g, '""')}"`).join(","),
      ...csvRows,
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [allColumns, columns, csvFields, exportCSV, flattenExportValue, prepareRow, rows]);

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);
  // UI v2 (design-reference "Travel · Bookings list"): one quiet toolbar row
  // over the table, a wider search, and a footer row with the count on the
  // left and the pages on the right.
  const isV2 = useTheme()?.pearlUi === "v2";

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    if (!column.canSort) return false;
    let sortedValue;
    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }
    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <MDBox display="flex" flexDirection="column" height="100%">
      <TableContainer sx={{ boxShadow: "none", flexGrow: 1, overflowY: "auto", overflowX: "auto", maxWidth: "100%" }}>
      {entriesPerPage || canSearch || exportCSV ? (
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={isV2 ? 0 : 3}
          sx={isV2 ? { px: 2, py: 1.5, gap: 1.5, flexWrap: "wrap", borderBottom: `1px solid ${v2t.border}` } : undefined}
        >
          {/* Left side: Filter Icon and Saved Filters */}
          {showFilters !== false && (
            <MDBox display="flex" alignItems="center" gap={1.5}>
              {/* Filter Icon - Opens filter drawer */}
              <Tooltip title="Filter" arrow placement="top">
                <IconButton
                  onClick={handleFilterIconClick}
                  size="small"
                  sx={isV2 ? v2IconButtonSx(activeFiltersCount > 0) : {
                    border: '1px solid #d2d6da',
                    borderRadius: '8px',
                    padding: '8px',
                    backgroundColor: activeFiltersCount > 0 ? '#e3f2fd' : 'transparent',
                    '&:hover': {
                      backgroundColor: activeFiltersCount > 0 ? '#bbdefb' : '#f5f5f5',
                      borderColor: 'info.main',
                    }
                  }}
                >
                  <Filter size={20} />
                </IconButton>
              </Tooltip>

              {/* Saved Filters Dropdown */}
              <MDBox display="flex" alignItems="center">
                <Tooltip title="Saved Filters" arrow placement="top">
                  <IconButton
                    onClick={handleSavedFilterClick}
                size="small"
                    disabled={loadingFilters}
                    sx={isV2 ? v2IconButtonSx(Boolean(selectedFilter)) : {
                      border: '1px solid #d2d6da',
                      borderRadius: '8px',
                      padding: '8px',
                      backgroundColor: selectedFilter ? '#e3f2fd' : 'transparent',
                      '&:hover': {
                        backgroundColor: selectedFilter ? '#bbdefb' : '#f5f5f5',
                        borderColor: 'info.main',
                      }
                    }}
                  >
                    {loadingFilters ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <BookmarkCheck
                        size={20}
                        // color={selectedFilter ? '#1565C8' : '#7b809a'}
                      />
                    )}
                  </IconButton>
                </Tooltip>
                
                {selectedFilter && (
                  <MDTypography variant="caption" color="info" sx={{ ml: 1, fontWeight: 'medium' }}>
                    {selectedFilter.name}
              </MDTypography>
                )}

                <Menu
                  anchorEl={savedFilterAnchorEl}
                  open={openSavedFilterMenu}
                  onClose={handleSavedFilterClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    style: {
                      maxHeight: 300,
                      width: '280px',
                      marginTop: '8px',
                    },
                  }}
                >
                  <MenuItem disabled sx={{ opacity: '1 !important' }}>
                    <ListItemText
                      primary="Saved Filters"
                      primaryTypographyProps={{
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        color: '#344767'
                      }}
                    />
                  </MenuItem>
                  
                  {loadingFilters ? (
                    <MenuItem disabled>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <MDTypography variant="caption">Loading filters...</MDTypography>
            </MDBox>
                    </MenuItem>
                  ) : savedFilters.length === 0 ? (
                    <MenuItem disabled>
                      <ListItemText
                        primary="No saved filters"
                        secondary={objectType ? `Create filters for ${objectType}` : "Select an object type"}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          color: '#7b809a'
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.75rem'
                        }}
                      />
                    </MenuItem>
                  ) : (
                    savedFilters.map((filter) => (
                      <MenuItem
                        key={filter.id}
                        selected={selectedFilter?.id === filter.id}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: '#e3f2fd',
                          },
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          pr: 1
                        }}
                      >
                        <MDBox 
                          onClick={() => handleFilterSelect(filter)}
                          sx={{ flexGrow: 1, cursor: 'pointer' }}
                        >
                          <ListItemText
                            primary={filter.name}
                            secondary={filter.description || `${filter.conditions?.length || 0} condition(s)`}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: selectedFilter?.id === filter.id ? 'medium' : 'regular'
                            }}
                            secondaryTypographyProps={{
                              fontSize: '0.75rem'
                            }}
                          />
                        </MDBox>
                        <MDBox display="flex" gap={0.5}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleEditFilter(filter, e)}
                            sx={{ 
                              p: 0.5,
                              color: 'info.main',
                              '&:hover': { backgroundColor: '#e3f2fd' }
                            }}
                          >
                            <Icon sx={{ fontSize: '1rem' }}>edit</Icon>
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteFilterClick(filter, e)}
                            sx={{ 
                              p: 0.5,
                              color: 'info',
                              '&:hover': { backgroundColor: '#e3f2fd' }
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </MDBox>
                      </MenuItem>
                    ))
                  )}
                  
                  {(selectedFilter || appliedFilters?.conditions?.length > 0) && (
                    <>
                      <MenuItem divider sx={{ height: '1px', padding: 0, margin: '8px 0' }} />
                      <MenuItem onClick={handleClearFilter} sx={{ color: '#f44336' }}>
                        <ListItemText
                          primary="Clear Filter"
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: 'medium'
                          }}
                        />
                      </MenuItem>
                    </>
                  )}
                </Menu>
              </MDBox>
            </MDBox>
          )}

          {/* Right side: Search, Entries Per Page, and Export */}
          <MDBox display="flex" alignItems="center" gap={isV2 ? 1.5 : 2} sx={isV2 ? { flexWrap: "wrap", ml: "auto" } : undefined}>
          {canSearch && isV2 && (
            <FilterSearch
              value={search ?? ""}
              placeholder="Search this list"
              width="20rem"
              onChange={(value) => {
                setSearch(value);
                onSearchChange(value);
              }}
            />
          )}

          {entriesPerPage && isV2 && (
            <FilterSelect
              label="Per page"
              value={pageSize.toString()}
              options={entries}
              onChange={(newValue) => {
                setEntriesPerPage(parseInt(newValue, 10));
              }}
            />
          )}

          {canSearch && !isV2 && (
              <MDBox width={isV2 ? "20rem" : "12rem"} sx={isV2 ? { maxWidth: "100%" } : undefined}>
              <MDInput
                placeholder={isV2 ? "Search this list" : "Search..."}
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(search);
                  onSearchChange(currentTarget.value);
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    height: '35px',
                  }
                }}
              />
              </MDBox>
            )}

            {/* Entries Per Page - Moved to right side */}
            {entriesPerPage && !isV2 && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => (
                  <MDInput 
                    {...params} 
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '35px',
                      }
                    }}
                  />
                )}
              />
              <MDTypography variant="caption" color="secondary" sx={isV2 ? { fontSize: 12, color: `${v2t.text2} !important`, whiteSpace: "nowrap" } : undefined}>
                &nbsp;&nbsp;{isV2 ? "per page" : "entries per page"}
              </MDTypography>
            </MDBox>
          )}

          {chooserEnabled && choosableColumns.length > 0 && (
            <>
              <Tooltip title="Columns" arrow placement="top">
                <IconButton
                  onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                  size="small"
                  aria-label="Choose columns"
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    padding: '8px',
                    color: 'text.secondary',
                    '&:hover': { borderColor: 'info.main', color: 'info.main' },
                  }}
                >
                  <Columns3 size={20} />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={() => setColumnMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { maxHeight: 420, width: 280, mt: 1 } }}
              >
                <MenuItem disabled sx={{ opacity: '1 !important' }}>
                  <ListItemText
                    primary="Columns"
                    secondary="Shown for you on this browser"
                    primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.875rem', color: 'text.primary' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </MenuItem>
                {choosableColumns.map((column) => {
                  const label = typeof column.Header === 'string' ? column.Header : column.id;
                  const isCustom = column.isExtraField && fieldMap[column.id]?.is_custom;
                  return (
                    <MenuItem key={column.id} dense onClick={() => toggleColumn(column)}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Checkbox
                          edge="start"
                          size="small"
                          checked={column.isVisible}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ 'aria-label': `Show ${label}` }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                      {isCustom && (
                        <Chip
                          label="Custom"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            backgroundColor: tokens.chip.selectedBackground,
                            color: tokens.chip.selectedText,
                          }}
                        />
                      )}
                    </MenuItem>
                  );
                })}
                <Divider sx={{ my: 0.5 }} />
                <MenuItem dense onClick={resetColumns}>
                  <ListItemText
                    primary="Reset to default"
                    primaryTypographyProps={{ fontSize: '0.875rem', color: 'info.main', fontWeight: 'medium' }}
                  />
                </MenuItem>
              </Menu>
            </>
          )}

          {exportCSV && (
              <Tooltip title="Export CSV" arrow placement="top">
              <Button
                variant={isV2 ? "outlined" : "gradient"}
                color="info"
                onClick={handleExportCSV}
                startIcon={<Download size={isV2 ? 16 : 20} />}
                sx={isV2 ? { minHeight: 36, borderColor: `${v2t.border2} !important`, color: `${v2t.text} !important`, backgroundColor: v2t.surface, fontWeight: 600, "&:hover": { backgroundColor: v2t.hover } } : undefined}
              >
                <span style={isV2 ? undefined : { color: 'green' }}>{isV2 ? "Export" : "CSV"}</span>
              </Button>
            </Tooltip>
            )}
          </MDBox>
        </MDBox>
      ) : null}
      <Table
        {...getTableProps()}
        ref={tableRef}
        sx={
          fixedLayout
            ? { tableLayout: "fixed", width: fixedTableWidth, minWidth: fixedTableWidth }
            : undefined
        }
      >
        {fixedLayout && (
          <colgroup>
            {visibleColumns.map((column) => (
              <col key={column.id} style={{ width: columnWidths[column.id] || 150 }} />
            ))}
          </colgroup>
        )}
        <MDBox component="thead">
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => {
                const isLeaf = !column.headers || column.headers.length === 0;
                const fixedWidth = fixedLayout && isLeaf ? columnWidths[column.id] || 150 : null;
                return (
                <DataTableHeadCell
                  key={idx}
                  {...column.getHeaderProps(isSorted && column.canSort && column.getSortByToggleProps())}
                  width={fixedWidth || (column.width ? column.width : "auto")}
                  align={column.align ? column.align : "left"}
                  sorted={column.canSort ? setSortedValue(column) : false}
                  colKey={isLeaf ? column.id : undefined}
                  fixedLayout={fixedLayout}
                  onResizeStart={isLeaf ? (e) => startResize(column.id, e) : undefined}
                  onResizeReset={isLeaf ? (e) => resetColumn(column.id, e) : undefined}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
                );
              })}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {filtersPending ? (
            <TableRow>
              <MDBox component="td" colSpan={visibleColumns.length} py={4}>
                <MDBox display="flex" justifyContent="center">
                  <PearlLogoLoader size="md" message="Applying filters..." />
                </MDBox>
              </MDBox>
            </TableRow>
          ) : page.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow
                key={key}
                {...row.getRowProps()}
                sx={{
                  "&:hover": { backgroundColor: "rgba(21, 101, 200, 0.04)" },
                }}
              >
                {row.cells.map((cell, idx) => (
                  <DataTableBodyCell
                    key={idx}
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    fixedLayout={fixedLayout}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MDBox
  display="flex"
  flexDirection={isV2 ? "row" : "column"}
  alignItems="center"
  justifyContent={isV2 ? "space-between" : "center"}
  p={!showTotalEntries && pageOptions.length === 1 ? 0 : isV2 ? 0 : 3}
  sx={isV2 && (showTotalEntries || pageOptions.length > 1) ? { px: 2, py: 1.5, gap: 1.5, flexWrap: "wrap", borderTop: `1px solid ${v2t.border}` } : undefined}
>
  {showTotalEntries && !filtersPending && (
    <MDBox mb={isV2 ? 0 : 2}>
      <MDTypography variant="button" color="secondary" fontWeight="regular" sx={isV2 ? { fontSize: 13, color: `${v2t.text2} !important` } : undefined}>
        Showing {entriesStart} to {entriesEnd} of {rows.length} entries
        {appliedFilters && appliedFilters.conditions && appliedFilters.conditions.length > 0 && rawData.length !== rows.length && (
          <span style={{ color: 'info.main', marginLeft: '4px' }}>
            (filtered from {rawData.length} total)
          </span>
        )}
      </MDTypography>
    </MDBox>
  )}
  {pageOptions.length > 1 && (
    <MDPagination
    variant={pagination.variant ? pagination.variant : "gradient"}
    color={pagination.color ? pagination.color : "info"}
  >
    {canPreviousPage && (
      <MDPagination item onClick={() => previousPage()}>
        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
      </MDPagination>
    )}
    {renderPagination.length > 6 ? (
      <MDBox width="5rem" mx={1}>
        <MDInput
          inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
          value={customizedPageOptions[pageIndex]}
          onChange={(handleInputPagination, handleInputPaginationValue)}
          sx={{
            '& .MuiInputBase-root': {
              height: '35px',
            }
          }}
        />
      </MDBox>
    ) : (
      renderPagination
    )}
    {canNextPage && (
      <MDPagination item onClick={() => nextPage()}>
        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
      </MDPagination>
    )}
  </MDPagination>
  )}
        </MDBox>
      </TableContainer>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={handleFilterDrawerClose}
        PaperProps={{
          sx: { 
            width: { xs: '100%', sm: 480, md: 520 }, 
            p: 0,
            boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
            overflowX: 'hidden'
          }
        }}
      >
        <MDBox sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
          {/* Header */}
          <MDBox 
            sx={{ 
              px: 3,
              py: 2.5, 
              borderBottom: '1px solid #e8ecf0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'info.main',
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <MDBox 
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  borderRadius: '10px', 
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Filter size={22} color="#fff" />
              </MDBox>
              <MDBox>
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: '#fff', fontSize: '1.1rem' }}>
                  {editingFilter ? `Edit: ${editingFilter.name}` : `Filter ${objectType ? objectType.charAt(0).toUpperCase() + objectType.slice(1) + 's' : 'Data'}`}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                  {availableFields.length} fields available
                </MDTypography>
              </MDBox>
            </MDBox>
            <IconButton 
              onClick={handleFilterDrawerClose} 
              size="small"
              sx={{ 
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <X size={20} />
            </IconButton>
          </MDBox>

          {/* Logic Operator Toggle */}
          <MDBox sx={{ px: 3, py: 2, borderBottom: '1px solid #e8ecf0', backgroundColor: '#f8fafc' }}>
            <MDBox display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <MDTypography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                Match
              </MDTypography>
              <MDBox display="flex" gap={1}>
                <Chip
                  label="All conditions (AND)"
                  size="small"
                  onClick={() => setLogicOperator('AND')}
                  sx={{
                    backgroundColor: logicOperator === 'AND' ? 'info.main' : '#e2e8f0',
                    color: logicOperator === 'AND' ? 'white !important' : '#475569',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    px: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: logicOperator === 'AND' ? '#1565c0' : '#cbd5e1',
                    }
                  }}
                />
                <Chip
                  label="Any condition (OR)"
                  size="small"
                  onClick={() => setLogicOperator('OR')}
                  sx={{
                    backgroundColor: logicOperator === 'OR' ? 'info.main' : '#e2e8f0',
                    color: logicOperator === 'OR' ? 'white !important' : '#475569',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    px: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: logicOperator === 'OR' ? '#1565c0' : '#cbd5e1',
                    }
                  }}
                />
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Filter Conditions */}
          <MDBox sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', px: 3, py: 2.5, backgroundColor: '#f8fafc' }}>
            {loadingFields ? (
              <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={6}>
                <PearlLogoLoader size="md" message="Loading fields..." />
              </MDBox>
            ) : availableFields.length === 0 ? (
              <MDBox 
                display="flex" 
                flexDirection="column" 
                justifyContent="center" 
                alignItems="center" 
                py={6}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 3,
                  border: '2px dashed #e2e8f0'
                }}
              >
                <Filter size={48} color="#cbd5e1" />
                <MDTypography variant="body1" sx={{ mt: 2, color: '#64748b', fontWeight: 500 }}>
                  No fields available
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: '#94a3b8', textAlign: 'center', px: 3 }}>
                  {objectType ? `No filterable fields found for ${objectType}` : 'Please select an object type'}
                </MDTypography>
              </MDBox>
            ) : (
              <>
                {filterConditions.map((condition, index) => (
                  <MDBox 
                    key={index} 
                    sx={{ 
                      mb: 2.5, 
                      p: 2.5, 
                      border: '1px solid #e2e8f0', 
                      borderRadius: 3,
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        borderColor: '#cbd5e1'
                      }
                    }}
                  >
                    {/* Condition Header */}
                    <MDBox display="flex"  alignItems="center" mb={2}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDBox 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            backgroundColor: 'info.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {index + 1}
                        </MDBox>
                        <MDTypography variant="body2" fontWeight="medium" sx={{ color: '#334155' }}>
                          Condition
                        </MDTypography>
                      </MDBox>
                      {filterConditions.length > 1 && (
                        <IconButton 
                          size="small" 
                          onClick={() => removeCondition(index)}
                          sx={{ 
                            color: '#ef4444',
                            backgroundColor: '#fef2f2',
                            width: 32,
                            height: 32,
                            '&:hover': { 
                              backgroundColor: '#fee2e2',
                            }
                          }}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      )}
                    </MDBox>

                    {/* Condition Inputs: Field, Operator, Value stacked vertically */}
                    <MDBox
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {/* Field Select */}
                      <FormControl fullWidth size="medium">
                        <InputLabel sx={{ fontSize: '0.875rem' }}>Select Field</InputLabel>
                        <Select
                          value={condition.field}
                          label="Select Field"
                          onChange={(e) => updateCondition(index, 'field', e.target.value)}
                          sx={{
                            backgroundColor: '#fff',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e2e8f0',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'info.main',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'info.main',
                              borderWidth: 2,
                            },
                            // increase visual height
                            height: '35px',
                            '& .MuiInputBase-input': {
                              paddingTop: 1.25,
                              paddingBottom: 1.25,
                            },
                            '& .MuiSelect-select': {
                              height: '35px',
                              minHeight: 44,
                              display: 'flex',
                              alignItems: 'center',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                '& .MuiMenuItem-root': {
                                  fontSize: '0.875rem',
                                  py: 1.5,
                                },
                              },
                            },
                          }}
                        >
                          {availableFields.map((field) => (
                            <MenuItem key={field.name} value={field.name}>
                              <MDBox
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  gap: 1,
                                }}
                              >
                                <MDTypography
                                  variant="body2"
                                  sx={{ fontWeight: 500, whiteSpace: 'normal', flexGrow: 1 }}
                                >
                                  {field.label}
                                </MDTypography>
                                <Chip
                                  label={field.type}
                                  size="small"
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 20,
                                    backgroundColor: '#e3f2fd',
                                    color: 'info.main',
                                    flexShrink: 0,
                                  }}
                                />
                              </MDBox>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Operator Select */}
                      {condition.field && (
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ fontSize: '0.875rem' }}>Select Operator</InputLabel>
                          <Select
                            value={condition.operator}
                            label="Select Operator"
                            onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                            sx={{
                              backgroundColor: '#fff',
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e2e8f0',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'info.main',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'info.main',
                                borderWidth: 2,
                              },
                              height: '35px',
                              '& .MuiInputBase-input': {
                                paddingTop: 1.25,
                                paddingBottom: 1.25,
                              },
                              '& .MuiSelect-select': {
                                height: '35px',
                                minHeight: 44,
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}
                          >
                            {getOperatorsForField(condition.field).map((op) => (
                              <MenuItem key={op.id} value={op.id}>
                                {op.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {/* Value Input */}
                      {condition.field &&
                        condition.operator &&
                        operatorNeedsValue(condition.operator) && (
                          <>
                            {isReferenceField(condition.field) ? (
                              (() => {
                                const field = availableFields.find(f => f.name === condition.field);
                                const referenceType = field?.reference_to;
                                const isUserRef = referenceType === 'users';
                                const options = isUserRef ? users : (referenceData[referenceType] || []);
                                const loading = isUserRef ? loadingUsers : (loadingReferenceData[referenceType] || false);
                                const label = isUserRef 
                                  ? 'Select User' 
                                  : `Select ${referenceType.charAt(0).toUpperCase() + referenceType.slice(1).replace(/s$/, '')}`;
                                
                                // Ensure we fetch data if not already loaded
                                if (referenceType && !isUserRef && (!referenceData[referenceType] || referenceData[referenceType].length === 0) && !loading) {
                                  fetchReferenceData(referenceType);
                                }
                                
                                return (
                                  <Autocomplete
                                    fullWidth
                                    options={options}
                                    getOptionLabel={(option) => {
                                      if (!option) return '';
                                      if (typeof option === 'string') return option;
                                      return option.label || option.name || 'Unknown';
                                    }}
                                    isOptionEqualToValue={(option, value) => {
                                      if (!option || !value) return false;
                                      return option.id === value.id;
                                    }}
                                    value={options.find(opt => opt && opt.id === parseInt(condition.value)) || null}
                                    onChange={(_, newValue) => {
                                      updateCondition(index, 'value', newValue ? newValue.id.toString() : '');
                                    }}
                                    onInputChange={(_, newInputValue) => {
                                      // Fetch reference data with search query when user types
                                      if (referenceType && !isUserRef && newInputValue.length >= 2) {
                                        fetchReferenceData(referenceType, newInputValue);
                                      } else if (referenceType && !isUserRef && newInputValue.length === 0) {
                                        // Reload all data when search is cleared
                                        fetchReferenceData(referenceType, '');
                                      }
                                    }}
                                    filterOptions={(options, params) => {
                                      // For user refs, use default filtering
                                      if (isUserRef) {
                                        return options;
                                      }
                                      // For other refs, filter client-side if we have all data
                                      // Otherwise, server-side filtering is handled by onInputChange
                                      const { inputValue } = params;
                                      if (!inputValue || inputValue.length < 2) {
                                        return options;
                                      }
                                      return options.filter(option =>
                                        option && option.label && option.label.toLowerCase().includes(inputValue.toLowerCase())
                                      );
                                    }}
                                    loading={loading}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={label}
                                        size="medium"
                                        placeholder={`Type to search ${referenceType}...`}
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                            borderRadius: 2,
                                            '& fieldset': { borderColor: '#e2e8f0' },
                                            '&:hover fieldset': { borderColor: 'info.main' },
                                            '&.Mui-focused fieldset': {
                                              borderColor: 'info.main',
                                              borderWidth: 2,
                                            },
                                            height: '35px',
                                            minHeight: 44,
                                          },
                                          '& .MuiInputBase-input': {
                                            paddingTop: 1.25,
                                            paddingBottom: 1.25,
                                          },
                                        }}
                                        InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                            <>
                                              {loading ? <CircularProgress size={20} /> : null}
                                              {params.InputProps.endAdornment}
                                            </>
                                          ),
                                        }}
                                      />
                                    )}
                                    noOptionsText={loading ? `Loading ${referenceType}...` : `No ${referenceType} found`}
                                    clearText="Clear selection"
                                  />
                                );
                              })()
                            ) : getFieldType(condition.field) === 'picklist' &&
                            getFieldOptions(condition.field).length > 0 ? (
                              <FormControl fullWidth size="medium">
                                <InputLabel sx={{ fontSize: '0.875rem' }}>
                                  Select Value
                                </InputLabel>
                                <Select
                                  value={condition.value}
                                  label="Select Value"
                                  onChange={(e) =>
                                    updateCondition(index, 'value', e.target.value)
                                  }
                                  sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#e2e8f0',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'info.main',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'info.main',
                                      borderWidth: 2,
                                    },
                                    height: '35px',
                                    '& .MuiInputBase-input': {
                                      paddingTop: 1.25,
                                      paddingBottom: 1.25,
                                    },
                                    '& .MuiSelect-select': {
                                      height: '35px',
                                      minHeight: 44,
                                      display: 'flex',
                                      alignItems: 'center',
                                    },
                                  }}
                                >
                                  {getFieldOptions(condition.field).map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : getFieldType(condition.field) === 'date' ||
                              getFieldType(condition.field) === 'datetime' ? (
                              <TextField
                                fullWidth
                                size="medium"
                                type="date"
                                label="Select Date"
                                value={condition.value}
                                onChange={(e) =>
                                  updateCondition(index, 'value', e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff',
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: '#e2e8f0' },
                                    '&:hover fieldset': { borderColor: 'info.main' },
                                    '&.Mui-focused fieldset': {
                                      borderColor: 'info.main',
                                      borderWidth: 2,
                                    },
                                    height: '35px',
                                    minHeight: 44,
                                  },
                                  '& .MuiInputBase-input': {
                                    paddingTop: 1.25,
                                    paddingBottom: 1.25,
                                  },
                                }}
                              />
                            ) : getFieldType(condition.field) === 'number' ||
                              getFieldType(condition.field) === 'currency' ? (
                              <TextField
                                fullWidth
                                size="medium"
                                type="number"
                                label="Enter Value"
                                placeholder="Enter a number"
                                value={condition.value}
                                onChange={(e) =>
                                  updateCondition(index, 'value', e.target.value)
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff',
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: '#e2e8f0' },
                                    '&:hover fieldset': { borderColor: 'info.main' },
                                    '&.Mui-focused fieldset': {
                                      borderColor: 'info.main',
                                      borderWidth: 2,
                                    },
                                    height: '35px',
                                    minHeight: 44,
                                  },
                                  '& .MuiInputBase-input': {
                                    paddingTop: 1.25,
                                    paddingBottom: 1.25,
                                  },
                                }}
                              />
                            ) : (
                              <TextField
                                fullWidth
                                size="medium"
                                label="Enter Value"
                                placeholder="Type a value..."
                                value={condition.value}
                                onChange={(e) =>
                                  updateCondition(index, 'value', e.target.value)
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff',
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: '#e2e8f0' },
                                    '&:hover fieldset': { borderColor: 'info.main' },
                                    '&.Mui-focused fieldset': {
                                      borderColor: 'info.main',
                                      borderWidth: 2,
                                    },
                                    height: '35px',
                                    minHeight: 44,
                                  },
                                  '& .MuiInputBase-input': {
                                    paddingTop: 1.25,
                                    paddingBottom: 1.25,
                                  },
                                }}
                              />
                            )}
                          </>
                        )}
                    </MDBox>
                  </MDBox>
                ))}

                {/* Add Condition Button */}
                <Button
                  variant="outlined"
                  startIcon={<Plus size={18} />}
                  onClick={addCondition}
                  fullWidth
                  sx={{ 
                    mt: 1,
                    py: 1.5,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: 'info.main',
                    color: 'info.main',
                    fontWeight: 600,
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: '#e3f2fd',
                    }
                  }}
                >
                  Add Another Condition
                </Button>
              </>
            )}
          </MDBox>

          {/* Footer Actions */}
          <MDBox 
            sx={{ 
              px: 3,
              py: 2.5, 
              borderTop: '1px solid #e8ecf0',
              display: 'flex',
              gap: 1.5,
              backgroundColor: '#fff',
              boxShadow: '0 -4px 12px rgba(0,0,0,0.05)'
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                resetFilterPanel();
                handleFilterDrawerClose();
              }}
              sx={{ 
                flex: 1,
                py: 1.25,
                borderColor: '#e2e8f0',
                color: '#64748b',
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              startIcon={<Save size={16} />}
              onClick={openSaveDialog}
              disabled={!objectType}
              sx={{ 
                flex: 1,
                py: 1.25,
                borderColor: 'info.main',
                color: 'info.main',
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: '#e3f2fd'
                }
              }}
            >
              {editingFilter ? 'Update' : 'Create'}
            </Button>
            {/* Apply without saving, as the Kanban drawer allows */}
            <Button
              variant="contained"
              onClick={applyFilters}
              sx={{
                flex: 1,
                py: 1.25,
                backgroundColor: 'info.main',
                color: 'white !important',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { backgroundColor: 'info.dark', boxShadow: 'none' }
              }}
            >
              Apply
            </Button>
          </MDBox>
        </MDBox>
      </Drawer>

      {/* Save Filter Dialog */}
      <Dialog 
        open={saveDialogOpen} 
        onClose={() => {
          setSaveDialogOpen(false);
          setEditingFilter(null);
          setFilterName('');
          setFilterDescription('');
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'info.main',
          color: '#fff',
          py: 2.5
        }}>
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDBox 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                borderRadius: '8px', 
                p: 0.75,
                display: 'flex'
              }}
            >
              <Save size={20} color="#fff" strokeWidth={2} />
            </MDBox>
            <MDTypography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
              {editingFilter ? 'Update Filter' : 'Save Filter'}
            </MDTypography>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <MDBox sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Filter Name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="e.g., High Value Deals"
              sx={{ 
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  height: '35px',
                  borderRadius: 2,
                  '&.Mui-focused fieldset': { borderColor: 'info.main', borderWidth: 2 }
                }
              }}
            />
            <TextField
              fullWidth
              label="Description (optional)"
              value={filterDescription}
              onChange={(e) => setFilterDescription(e.target.value)}
              placeholder="e.g., Deals worth more than $10,000"
              multiline
              rows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': { borderColor: 'info.main', borderWidth: 2 }
                }
              }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #e8ecf0' }}>
          <Button 
            onClick={() => setSaveDialogOpen(false)}
            sx={{ 
              color: '#64748b',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={editingFilter ? updateFilter : saveFilter}
            disabled={savingFilter || !filterName.trim()}
            sx={{ 
              backgroundColor: 'info.main',
              fontWeight: 600,
              px: 4,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            {savingFilter ? <CircularProgress size={20} color="inherit" /> : (editingFilter ? 'Update Filter' : 'Save Filter')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter feedback dialog (replaces browser alert) */}
      <Dialog
        open={feedbackDialog.open}
        onClose={closeFilterFeedback}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor:
              feedbackDialog.severity === 'error'
                ? 'error.main'
                : 'info.main',
            color: '#fff',
            m: 0,
            px: 3,
            py: 2,
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.4,
            display: 'flex',
            alignItems: 'center',
            minHeight: 56,
          }}
        >
          {feedbackDialog.title}
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            py: 2.5,
            flex: '0 0 auto',
            '&.MuiDialogContent-root': {
              paddingTop: 2.5,
            },
          }}
        >
          <MDTypography variant="body2" color="text" sx={{ lineHeight: 1.6 }}>
            {feedbackDialog.message}
          </MDTypography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            m: 0,
            borderTop: '1px solid #e8ecf0',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={closeFilterFeedback}
            sx={{
              backgroundColor:
                feedbackDialog.severity === 'error'
                  ? 'error.main'
                  : 'info.main',
              color: '#fff !important',
              fontWeight: 600,
              minWidth: 96,
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor:
                  feedbackDialog.severity === 'error'
                    ? '#d32f2f'
                    : '#1565c0',
                boxShadow: 'none',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setFilterToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold" color="error">
            Delete Filter
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            Are you sure you want to delete the filter "{filterToDelete?.name}"? This action cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setFilterToDelete(null);
            }}
            sx={{ color: '#64748b', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => deleteFilter(filterToDelete?.id)}
            disabled={deletingFilter}
            sx={{ 
              color: 'white !important',
              backgroundColor: '#ef4444',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#dc2626' }
            }}
          >
            {deletingFilter ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  exportCSV: false,
  csvFields: [],
  objectType: null,
  onFilterApply: null,
  onFilterClear: null,
  showFilters: true,
  onFilterIconClick: null,
  columnChooser: true,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  exportCSV: PropTypes.bool,
  csvFields: PropTypes.arrayOf(PropTypes.string),
  // Filter-related props
  objectType: PropTypes.string, // Object type for fetching saved filters (e.g., "lead", "deal", "account")
  onFilterApply: PropTypes.func, // Callback when a saved filter is applied
  onFilterClear: PropTypes.func, // Callback when filter is cleared
  showFilters: PropTypes.bool, // Whether to show filter icons
  onFilterIconClick: PropTypes.func, // Callback when filter icon is clicked (to open filter modal)
  columnChooser: PropTypes.bool, // Show the per-user column chooser (needs objectType)
};

export default DataTable;
